import {
  deleteObject,
  getDownloadURL,
  getStorage,
  ref,
  uploadBytes,
  uploadString,
} from 'firebase/storage';
import { InputFileType } from '../../../types/input';
import { PatternFileType } from '../../../types/pattern';
import { storage } from '../config';
import { captureAsyncError } from '../../../utils/errorHandler';
import { ValidationError } from '../../../modules/error';

// 読取パターンファイルの表示用URLを解決する。
// download_url があればそれを使い、無ければ storage_path から取得する（欠落時のフォールバック）。
export const resolvePatternFileUrl = async (file: PatternFileType): Promise<string | null> => {
  if (file.download_url) return file.download_url;
  if (!file.storage_path) return null;
  try {
    return await getDownloadURL(ref(storage, file.storage_path));
  } catch {
    return null;
  }
};

// ファイルが保存されているstorageのURLを取得する
export const getFileUrl = async (file: InputFileType): Promise<string> => {
  const storage = getStorage();

  try {
    const fileRef = ref(storage, file.file_path);
    return await getDownloadURL(fileRef);
  } catch (error) {
    throw new Error('ファイルの取得に失敗しました。もう一度お試しください。');
  }
};

// ファイルをstorageにアップロードする
export const postFile = captureAsyncError(
  async (
    file: File,
    userId: string,
    inputId: string,
  ): Promise<{
    fileName: string;
    storagePath: string;
  }> => {
    const fileName = `${Date.now()}_${file.name}`;
    const storagePath = `uploads/${userId}/${inputId}/${fileName}`;
    const storageRef = ref(storage, storagePath);

    await uploadBytes(storageRef, file);
    return { fileName, storagePath };
  },
);

export const uploadFile = captureAsyncError(
  async (
    fileIcon: FileList | null,
    storagePath: string,
  ): Promise<{
    storagePath: string;
    iconUrl: string;
  }> => {
    if (!fileIcon || fileIcon.length === 0) {
      throw new ValidationError('ファイルが選択されていません。');
    }

    const file = fileIcon[0];
    const storageRef = ref(storage, storagePath);

    // ファイルをアップロード
    await uploadBytes(storageRef, file);
    const iconUrl = await getDownloadURL(storageRef);

    return { storagePath, iconUrl };
  },
);

export const deleteFile = async (storagePath: string): Promise<void> => {
  if (!storagePath) {
    throw new Error('削除するファイルパスが指定されていません。');
  }

  const fileRef = ref(storage, storagePath);

  try {
    await deleteObject(fileRef);
  } catch (error) {
    throw new Error('ファイルの削除に失敗しました。もう一度お試しください。');
  }
};

export const clientMasterUpdate = async (
  clientName: string,
  postCode = '',
  address = '',
): Promise<number> => {
  const storage = getStorage();
  const path = 'master/顧客マスタ.csv';
  const fileRef = ref(storage, path);

  // CSVを取得
  const url = await getDownloadURL(fileRef);
  const response = await fetch(url);
  const text = await response.text();

  // CSVをパース
  const rows = text
    .split('\n')
    .filter(row => row.trim() !== '')
    .map(row => row.split(','));

  // 最終行のID取得（最終行はヘッダーを含まない前提）
  const lastRow = rows[rows.length - 1];
  const lastId = lastRow ? Number(lastRow[0]) || 0 : 0;
  const newId = lastId + 1;

  // 新規レコード作成
  const newRecord = `${newId},${clientName},${postCode},${address}\n`;
  const updatedCSV = text.endsWith('\n') ? text + newRecord : text + '\n' + newRecord;

  // CSVをストレージにアップロード
  await uploadString(fileRef, updatedCSV);

  return newId;
};

export const productMasterUpdate = async (productName: string, unitPrice = 0): Promise<number> => {
  const storage = getStorage();
  const path = 'master/商品マスタ.csv';
  const fileRef = ref(storage, path);

  // CSV取得
  const url = await getDownloadURL(fileRef);
  const response = await fetch(url);
  const text = await response.text();

  // CSVをパース
  const rows = text
    .split('\n')
    .filter(row => row.trim() !== '')
    .map(row => row.split(','));

  // 最終行のID取得（最終行はヘッダーを含まない前提）
  const lastRow = rows[rows.length - 1];
  const lastId = lastRow ? Number(lastRow[0]) || 0 : 0;
  const newId = lastId + 1;

  // 新規レコード作成
  const newRecord = `${newId},${productName},${unitPrice}\n`;
  const updatedCSV = text.endsWith('\n') ? text + newRecord : text + '\n' + newRecord;

  // CSVをストレージにアップロード
  await uploadString(fileRef, updatedCSV);

  return newId;
};

// 読取パターン関連のストレージ機能

// Firebase Storageに読取パターンファイルをアップロードする
export const uploadPatternsToStorage = async (
  files: File[],
  patternId: string,
  subfolder = 'samples',
  companyId?: string,
): Promise<PatternFileType[]> => {
  const uploadedFiles: PatternFileType[] = [];

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const fileName = `${Date.now()}_${i}_${file.name}`;
    const storagePath = companyId
      ? `patterns/${companyId}/${patternId}/${subfolder}/${fileName}`
      : `patterns/${patternId}/${subfolder}/${fileName}`;
    const storageRef = ref(storage, storagePath);

    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);

    uploadedFiles.push({
      file_name: file.name,
      file_size: file.size,
      file_type: file.type,
      storage_path: storagePath,
      download_url: downloadURL,
    });
  }

  return uploadedFiles;
};

// Firebase Storageから読取パターンファイルを削除する
export const deletePatternFilesFromStorage = async (files: PatternFileType[]): Promise<void> => {
  for (const file of files) {
    const storageRef = ref(storage, file.storage_path);
    await deleteObject(storageRef);
  }
};

// 既存の読取パターンファイルを新しい読取パターン用にコピーする
export const copyPatternsToStorage = async (
  existingFiles: PatternFileType[],
  newPatternId: string,
  subfolder = 'samples',
  companyId?: string,
): Promise<PatternFileType[]> => {
  const copiedFiles: PatternFileType[] = [];

  for (let i = 0; i < existingFiles.length; i++) {
    const existingFile = existingFiles[i];

    try {
      // 既存ファイルのダウンロードURL取得
      const existingRef = ref(storage, existingFile.storage_path);
      const downloadURL = await getDownloadURL(existingRef);

      // ファイルをfetchしてBlobとして取得
      const response = await fetch(downloadURL);
      const blob = await response.blob();

      // 新しいファイル名とパスを生成
      const fileName = `${Date.now()}_${i}_${existingFile.file_name}`;
      const newStoragePath = companyId
        ? `patterns/${companyId}/${newPatternId}/${subfolder}/${fileName}`
        : `patterns/${newPatternId}/${subfolder}/${fileName}`;
      const newStorageRef = ref(storage, newStoragePath);

      // 新しい場所にアップロード
      await uploadBytes(newStorageRef, blob);
      const newDownloadURL = await getDownloadURL(newStorageRef);

      copiedFiles.push({
        file_name: existingFile.file_name,
        file_size: existingFile.file_size,
        file_type: existingFile.file_type,
        storage_path: newStoragePath,
        download_url: newDownloadURL,
      });
    } catch (error) {
      throw new Error(
        `ファイル ${existingFile.file_name} のコピーに失敗しました。もう一度お試しください。`,
      );
    }
  }

  return copiedFiles;
};
